/**
 * Auto-layout utilities for flowchart nodes
 * 
 * This is the main entry point that re-exports from specialized modules:
 * - graphTraversal.js: Finding connected nodes, BFS traversal
 * - tetrisCompaction.js: Horizontal chain compaction
 * - chainSwap.js: Chain reordering when < > buttons clicked
 */

import { calculateLayout } from '../../layout';
import { HORIZONTAL_GAP, PADDING, getNodeDimensions } from '../../layout/constants';

// Re-export from specialized modules
export { findConnectedNodes } from './graphTraversal';
export { compactChainsHorizontally } from './tetrisCompaction';
export {
  swapChainsAndCompact,
  buildPositionsForChainSwap,
  calculateTetrisPositionRight,
} from './chainSwap';

/**
 * Perform auto-layout on selected nodes
 * 
 * - No selection OR all selected → Returns empty object (full auto-layout by engine)
 * - Some selected → Finds connected subgraph, applies full tidy, inserts at original position
 * 
 * Groups:
 * - Group A: Selected/connected nodes - tidied as if alone
 * - Group B: Unselected nodes to the LEFT of selection - stay where they are
 * - Group C: Unselected nodes to the RIGHT of selection - moved as a group after Group A
 * 
 * Tetris compaction runs afterward to close gaps.
 * 
 * @param {Object} params
 * @param {Set|null} params.selectedNodeIds - IDs of selected nodes
 * @param {Array} params.tasks - All tasks
 * @param {Object} params.nodePositions - Auto-generated positions
 * @param {Object} params.customPositions - Custom positions (overrides)
 * @param {Object} params.nodeDimensions - Node dimensions map
 * @returns {Object} New positions map (empty for full reset, or updated positions)
 */
export function calculateAutoLayout({
  selectedNodeIds,
  tasks,
  nodePositions,
  customPositions,
  nodeDimensions,
}) {
  // Import here to avoid circular dependency
  const { findConnectedNodes } = require('./graphTraversal');
  
  const hasSelection = selectedNodeIds && selectedNodeIds.size > 0;
  const allSelected = hasSelection && selectedNodeIds.size === tasks.length;
  
  // No selection or all selected = full auto-layout (clear all custom positions)
  if (!hasSelection || allSelected) {
    return {};
  }
  
  // STEP 1: Find all nodes connected to the selection (Group A)
  const connectedNodeIds = findConnectedNodes(selectedNodeIds, tasks);
  
  // If all nodes are connected, just do a full tidy
  if (connectedNodeIds.size === tasks.length) {
    return {};
  }
  
  // STEP 2: Find the original leftmost X of the selected nodes
  let originalLeftmostX = Infinity;
  
  connectedNodeIds.forEach(taskId => {
    const pos = customPositions[taskId] || nodePositions[taskId];
    if (pos) {
      originalLeftmostX = Math.min(originalLeftmostX, pos.x);
    }
  });
  
  if (originalLeftmostX === Infinity) {
    originalLeftmostX = PADDING;
  }
  
  // STEP 3: Categorize unselected nodes into Group B (left) and Group C (right)
  const groupB = []; // Nodes to the left of selection - stay put
  const groupC = []; // Nodes to the right of selection - will be moved
  
  tasks.forEach(task => {
    if (connectedNodeIds.has(task.id)) return;
    
    const pos = customPositions[task.id] || nodePositions[task.id];
    if (!pos) return;
    
    // A node is in Group B if its position is to the left of the original selection's leftmost X
    // A node is in Group C if its position is at or to the right of the original selection's leftmost X
    if (pos.x < originalLeftmostX) {
      groupB.push({ task, pos });
    } else {
      groupC.push({ task, pos });
    }
  });
  
  // STEP 4: Apply full tidy layout to Group A (connected subgraph)
  const connectedTasks = tasks.filter(t => connectedNodeIds.has(t.id));
  const layoutResult = calculateLayout(connectedTasks, nodeDimensions);
  const subgraphPositions = layoutResult.positions || layoutResult;
  
  // STEP 5: Calculate the subgraph's dimensions and build node info
  let subgraphMinX = Infinity;
  let subgraphMaxX = -Infinity;
  const groupANodes = []; // For overlap detection
  
  Object.entries(subgraphPositions).forEach(([taskId, pos]) => {
    subgraphMinX = Math.min(subgraphMinX, pos.x);
    const dims = getNodeDimensions(taskId, nodeDimensions);
    subgraphMaxX = Math.max(subgraphMaxX, pos.x + dims.width);
    groupANodes.push({
      id: taskId,
      x: pos.x,
      y: pos.y,
      width: dims.width,
      height: dims.height,
    });
  });
  
  // STEP 6: Place Group A at the original leftmost position (initial offset)
  let offsetX = originalLeftmostX - subgraphMinX;
  
  // STEP 6b: Check if Group A overlaps with any Group B nodes
  // If so, shift Group A right until there's no overlap
  // This handles the case where the new layout extends into Y ranges occupied by Group B
  if (groupB.length > 0) {
    let maxRequiredShift = 0;
    
    groupANodes.forEach(nodeA => {
      // Position of this node after applying offsetX
      const nodeAX = nodeA.x + offsetX;
      const nodeARight = nodeAX + nodeA.width;
      const nodeAY = nodeA.y;
      const nodeABottom = nodeA.y + nodeA.height;
      
      groupB.forEach(({ task, pos }) => {
        const dims = getNodeDimensions(task.id, nodeDimensions);
        const nodeBRight = pos.x + dims.width;
        const nodeBBottom = pos.y + dims.height;
        
        // Check if they overlap vertically (Y ranges intersect)
        const verticalOverlap = nodeAY < nodeBBottom && nodeABottom > pos.y;
        
        if (verticalOverlap) {
          // Check if they overlap or are too close horizontally
          // Node A should be at least HORIZONTAL_GAP to the right of Node B's right edge
          const requiredX = nodeBRight + HORIZONTAL_GAP;
          if (nodeAX < requiredX) {
            // Need to shift Group A right
            const shift = requiredX - nodeAX;
            maxRequiredShift = Math.max(maxRequiredShift, shift);
          }
        }
      });
    });
    
    // Apply the additional shift if needed
    if (maxRequiredShift > 0) {
      offsetX += maxRequiredShift;
    }
  }
  
  const groupARightEdge = subgraphMaxX + offsetX;
  
  // STEP 7: Build the new positions map
  const newPositions = {};
  
  // Group B: Keep them where they are
  groupB.forEach(({ task, pos }) => {
    newPositions[task.id] = { ...pos };
  });
  
  // Group A: Add the repositioned connected nodes (with adjusted offset)
  Object.entries(subgraphPositions).forEach(([taskId, pos]) => {
    newPositions[taskId] = {
      x: Math.round(pos.x + offsetX),
      y: Math.round(pos.y),
    };
  });
  
  // Group C: Move as a group so their left edge is after Group A's right edge
  if (groupC.length > 0) {
    // Find Group C's current leftmost X
    let groupCLeftmostX = Infinity;
    groupC.forEach(({ pos }) => {
      groupCLeftmostX = Math.min(groupCLeftmostX, pos.x);
    });
    
    // Calculate how much to shift Group C
    const groupCTargetX = groupARightEdge + HORIZONTAL_GAP;
    const groupCShift = groupCTargetX - groupCLeftmostX;
    
    // Apply shift to all Group C nodes
    groupC.forEach(({ task, pos }) => {
      newPositions[task.id] = {
        x: Math.round(pos.x + groupCShift),
        y: pos.y,
      };
    });
  }
  
  return newPositions;
}
